import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

// Grounded smart search input: real-entity autosuggest + "did you mean" correction.
// Suggestions come only from published products/services/destinations — never invented.
export const SmartSuggest = ({
  scope = "products",
  value,
  onChange,
  onSubmit,
  onPick,
  placeholder = "Search…",
  testid = "smart",
  className = "",
  inputClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const tRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!value || value.trim().length < 2) { setData(null); return; }
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      api.get(`/marketplace/public/suggest?scope=${scope}&q=${encodeURIComponent(value.trim())}`)
        .then((r) => { setData(r.data); setOpen(true); })
        .catch(() => setData(null));
    }, 220);
    return () => clearTimeout(tRef.current);
  }, [value, scope]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const flat = [];
  if (data) {
    (data.pages || []).forEach((p) => flat.push({ label: p.label, type: "page", route: p.route, sub: p.desc }));
    (data.products || []).forEach((p) => flat.push({ label: p.name, type: "product", id: p.product_id }));
    (data.services || []).forEach((s) => flat.push({ label: s.name, type: "service", id: s.service_id, slug: s.slug }));
    (data.destinations || []).forEach((d) => flat.push({ label: d.name, type: "destination", sub: d.country }));
    (data.categories || []).forEach((c) => flat.push({ label: c.name, type: "category", id: c.category_id }));
    (data.brands || []).forEach((b) => flat.push({ label: b, type: "brand" }));
  }

  const choose = (it) => {
    setOpen(false);
    if (it.route) { nav(it.route); return; }
    if (onPick) onPick(it);
    else { onChange(it.label); if (onSubmit) onSubmit(it.label); }
  };

  const applyDidYouMean = () => { onChange(data.did_you_mean); setHi(-1); };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setHi(-1); }}
        onFocus={() => { if (flat.length || data?.did_you_mean) setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, flat.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, -1)); }
          else if (e.key === "Enter") { if (hi >= 0 && flat[hi]) { e.preventDefault(); choose(flat[hi]); } else { setOpen(false); if (onSubmit) onSubmit(value); } }
          else if (e.key === "Escape") { setOpen(false); }
        }}
        placeholder={placeholder}
        data-testid={`${testid}-input`}
        className={inputClassName}
      />
      {open && data && (flat.length > 0 || data.did_you_mean) && (
        <div className="absolute left-0 z-40 mt-1 max-h-80 w-full min-w-[16rem] overflow-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg" data-testid={`${testid}-dropdown`}>
          {data.did_you_mean && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={applyDidYouMean} data-testid={`${testid}-didyoumean`}
              className="m-1 block w-[calc(100%-0.5rem)] rounded-lg bg-amber-50 px-3 py-1.5 text-left text-xs text-amber-700 hover:bg-amber-100">
              Did you mean <span className="font-bold">{data.did_you_mean}</span>?
            </button>
          )}
          {flat.map((it, i) => (
            <button key={`${it.type}-${i}`} type="button" onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setHi(i)} onClick={() => choose(it)} data-testid={`${testid}-opt-${i}`}
              className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm ${hi === i ? "bg-slate-100" : "hover:bg-slate-50"}`}>
              <span className="truncate text-slate-800">{it.label}{it.sub ? <span className="ml-1 text-xs text-slate-400">· {it.sub}</span> : null}</span>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{it.type}</span>
            </button>
          ))}
          {flat.length === 0 && !data.did_you_mean && <p className="px-3 py-2 text-xs text-slate-400">No matches — press Enter to search.</p>}
        </div>
      )}
    </div>
  );
};

export default SmartSuggest;
